import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Modal } from 'semantic-ui-react';
import { Link } from 'react-router-dom';

import styles from './MonthlyReportModal.module.scss';
import { formatPoints } from '../../utils/formatPoints';
import Paths from '../../constants/Paths';
import { calculateCoefficient } from '../../utils/calculateCoefficient';
import computeEmployeeLevelsByAssignees from '../../utils/compute-employee-levels-by-assignees';
import computeEmployeeLevel from '../../utils/compute-employee-level';
import isCardInList from '../../utils/is-card-in-list';
import getViewsBase from '../../utils/get-views-base';

const WORKED_DAYS_INPUT_PATTERN = /^\d+(\.\d+)?$/;

const isValidWorkedDaysValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  const normalized = String(value).trim().replace(',', '.');
  if (!WORKED_DAYS_INPUT_PATTERN.test(normalized)) {
    return false;
  }

  const num = Number(normalized);
  return Number.isFinite(num) && num >= 0;
};

const normalizeWorkedDaysInput = (value) => String(value).trim().replace(',', '.');

const formatAssigneeNames = (assignees) => {
  if (!Array.isArray(assignees) || assignees.length === 0) {
    return '—';
  }

  return assignees
    .map((assignee) => {
      const name = assignee?.name || assignee?.username || assignee?.email || '';
      if (!name) {
        return '';
      }

      const organization =
        typeof assignee?.organization === 'string' ? assignee.organization.trim() : '';
      return organization ? `${name} (${organization})` : name;
    })
    .filter(Boolean)
    .join(', ');
};

const CATEGORY_DISPLAY_TITLES = {
  ФОТО: 'Фото',
  ВИДЕО: 'Видео',
  КЕЙСЫ: 'Кейсы',
};

const formatCategoryTitle = (category) => CATEGORY_DISPLAY_TITLES[category] || category || '—';

const MonthTitles = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

const buildMonthOptions = () =>
  MonthTitles.map((text, idx) => ({
    key: idx + 1,
    text,
    value: idx + 1,
  }));

const buildYearOptions = (years) =>
  (years || []).map((y) => ({
    key: y,
    text: String(y),
    value: y,
  }));

const normalizePeriodValue = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const num = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isNaN(num) ? null : num;
};

const normalizePeriod = (yearValue, monthValue) => ({
  year: normalizePeriodValue(yearValue),
  month: normalizePeriodValue(monthValue),
});

const formatDate = (value) => {
  if (!value) {
    return '';
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }

  return d.toLocaleDateString('ru-RU');
};

const formatMonthYearTitle = (monthValue, yearValue) => {
  const monthNum = normalizePeriodValue(monthValue);
  const yearNum = normalizePeriodValue(yearValue);
  if (!monthNum || !yearNum || monthNum < 1 || monthNum > 12) {
    return '—';
  }

  const monthTitle = MonthTitles[monthNum - 1] || `Месяц ${monthNum}`;
  return `${monthTitle.toLowerCase()} ${yearNum}`;
};

const getMonthYearFromDate = (value) => {
  if (!value) {
    return null;
  }

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return null;
  }

  // Месяц зачёта в отчёте считается по UTC-диапазону (см. monthly-reports/show)
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
  };
};

const StatRow = React.memo(({ label, value, valueClassName }) => (
  <div className={styles.statRow}>
    <div className={styles.statLabel}>{label}</div>
    <div className={`${styles.statValue} ${valueClassName || ''}`}>{value}</div>
  </div>
));

StatRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  valueClassName: PropTypes.string,
};

const CategorySummaryCard = React.memo(({ title, count, points, views }) => (
  <div className={styles.categoryCard}>
    <div className={styles.categoryTitle}>{formatCategoryTitle(title)}</div>
    <div className={styles.categoryStats}>
      <StatRow label="Количество" value={count} />
      <StatRow label="Баллы" value={formatPoints(points)} />
      <StatRow label="Просмотры" value={views} />
    </div>
  </div>
));

CategorySummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  views: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const TaskMetaRow = React.memo(({ label, value, valueClassName }) => (
  <div className={styles.taskMetaRow}>
    <div className={styles.taskMetaLabel}>{label}</div>
    <div className={`${styles.taskMetaValue} ${valueClassName || ''}`}>{value}</div>
  </div>
));

TaskMetaRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]).isRequired,
  valueClassName: PropTypes.string,
};

const TaskBlock = React.memo(({ task }) => {
  const coefficientNum = Number(task.coefficient);
  const isCoefficientFinite = Number.isFinite(coefficientNum);
  const coefficientText = isCoefficientFinite ? coefficientNum.toFixed(2) : task.coefficient;

  const coefficientClassName = isCoefficientFinite
    ? coefficientNum < 1
      ? styles.valueBad
      : styles.valueGood
    : null;

  const extraPointsSum = Array.isArray(task.extraPoints)
    ? task.extraPoints.reduce((sum, p) => sum + (Number(p?.value) || 0), 0)
    : 0;

  const coefficientPoints = isCoefficientFinite
    ? (Number(task.basePoints) + extraPointsSum) * (coefficientNum - 1)
    : Number(task.totalPoints) - (Number(task.basePoints) + extraPointsSum);

  const extraPointsDescriptions = Array.isArray(task.extraPoints)
    ? task.extraPoints
        .map((p) => (typeof p?.description === 'string' ? p.description.trim() : ''))
        .filter(Boolean)
    : [];

  const extraReasonText =
    (typeof task.comment === 'string' && task.comment.trim()) ||
    extraPointsDescriptions.join('; ') ||
    '';

  const extraPointsAbs = Math.abs(extraPointsSum);
  const extraPointsSigned =
    extraPointsSum === 0
      ? '0'
      : `${extraPointsSum > 0 ? '+' : '−'}${formatPoints(extraPointsAbs)}`;
  const extraPointsClassName =
    extraPointsSum > 0 ? styles.valueGood : extraPointsSum < 0 ? styles.valueBad : null;

  return (
    <div className={styles.taskBlock}>
      <div className={styles.taskHeader}>
        <div className={styles.taskHeaderLeft}>
          <div className={styles.taskNumber}>{`Задача №${task.number}`}</div>
          <div className={styles.taskName}>{task.name}</div>
          {task.sourceTemplateName && task.isPreviousTemplate && (
            <span className={styles.previousTemplateBadge}>{task.sourceTemplateName}</span>
          )}
        </div>
        <div className={styles.taskHeaderRight}>
          <div className={styles.taskDateRow}>
            <span className={styles.taskDateLabel}>Дата создания</span>
            <span className={styles.taskDateValue}>{task.createdAt || '—'}</span>
          </div>
          <div className={styles.taskDateRow}>
            <span className={styles.taskDateLabel}>Дата завершения</span>
            <span className={styles.taskDateValue}>{task.closedAt || '—'}</span>
          </div>
        </div>
      </div>

      <div className={styles.taskGrid}>
        <div className={styles.taskCol}>
          <TaskMetaRow label="Исполнитель" value={formatAssigneeNames(task.assignees)} />
          <TaskMetaRow label="Категория" value={formatCategoryTitle(task.category)} />
          <TaskMetaRow label="Тип" value={task.type || '—'} />
          <TaskMetaRow label="Просмотры" value={task.views} />
        </div>

        <div className={styles.taskCol}>
          <TaskMetaRow label="Основные баллы" value={formatPoints(task.basePoints)} />
          <TaskMetaRow
            label="Дополнительные баллы"
            value={extraPointsSigned}
            valueClassName={extraPointsClassName}
          />
          <TaskMetaRow
            label="Назначенный КФ"
            value={coefficientText}
            valueClassName={coefficientClassName}
          />
          <TaskMetaRow label="Баллы по КФ" value={formatPoints(coefficientPoints)} />
        </div>

        <div className={styles.taskTotalCol}>
          <div className={styles.taskTotalLabel}>Итого за задачу</div>
          <div className={styles.taskTotalValue}>{formatPoints(task.totalPoints)}</div>
          <div className={styles.taskTotalHint}>баллов с учётом КФ</div>
        </div>
      </div>

      {extraPointsSum !== 0 && (
        <div className={styles.taskComment}>
          <div className={styles.taskCommentTitle}>
            {extraPointsSum > 0
              ? `За что добавлены ${formatPoints(extraPointsAbs)} баллов`
              : `За что сняты ${formatPoints(extraPointsAbs)} баллов`}
          </div>
          {extraReasonText ? (
            <div className={styles.taskCommentText}>{extraReasonText}</div>
          ) : null}
        </div>
      )}
    </div>
  );
});

TaskBlock.propTypes = {
  task: PropTypes.shape({
    number: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    closedAt: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    basePoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    extraPoints: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        description: PropTypes.string,
      }),
    ).isRequired,
    views: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    coefficient: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    totalPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    comment: PropTypes.string.isRequired,
    assignees: PropTypes.array,
    sourceTemplateName: PropTypes.string,
    isPreviousTemplate: PropTypes.bool,
  }).isRequired,
};

const TaskNameOnly = React.memo(({ task, linkState }) => {
  if (task.cardId) {
    return (
      <Link
        to={Paths.CARDS.replace(':id', task.cardId)}
        state={linkState}
        className={styles.taskBadge}
      >
        <span className={styles.taskName}>{task.name}</span>
      </Link>
    );
  }

  return (
    <span className={styles.taskBadge}>
      <span className={styles.taskName}>{task.name}</span>
    </span>
  );
});

TaskNameOnly.propTypes = {
  task: PropTypes.shape({
    name: PropTypes.string.isRequired,
    cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  linkState: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

TaskNameOnly.defaultProps = {
  linkState: undefined,
};

const OtherMonthTaskItem = React.memo(({ task, listMonthTitle }) => {
  const taskMonthTitle = formatMonthYearTitle(task.creditMonth, task.creditYear);
  const hint = `Месяц зачёта задачи — ${taskMonthTitle}, а у списка — ${listMonthTitle}`;

  return (
    <div className={styles.otherMonthTaskItem}>
      <TaskNameOnly task={task} linkState={{ warnClosedAtMismatch: true }} />
      <div className={styles.otherMonthTaskHint}>{hint}</div>
    </div>
  );
});

OtherMonthTaskItem.propTypes = {
  task: PropTypes.shape({
    name: PropTypes.string.isRequired,
    cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    creditYear: PropTypes.number,
    creditMonth: PropTypes.number,
  }).isRequired,
  listMonthTitle: PropTypes.string.isRequired,
};

const filterTasksByCurrentList = (tasks, currentListCardIds) => {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return [];
  }

  return tasks.filter((task) => isCardInList(currentListCardIds, task.cardId));
};

const MonthlyReportModal = React.memo(
  ({ open, pointSystem, listId, report, isReportLoading, onReportFetch, onClose, unclosedTasks, allUnclosedTasksInList, onCompleteMonth, contentViewPlans, onContentViewPlansFetch, onEmployeePlansFetch, onDampingCoefficientsFetch, onOpenUnclosedTasksModal, onListReportDataFetch, onListReportDataUpsert, onListReportEmployeeLevelsUpdate, listReportData, employeePlans, dampingCoefficients, contentTypes, cardAssigneesByCardId, tasksWithoutAssignee, currentListCardIds }) => {
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [workedDaysDrafts, setWorkedDaysDrafts] = useState({});
  const [workedDaysErrors, setWorkedDaysErrors] = useState({});
  const [employeeLevelsValidationError, setEmployeeLevelsValidationError] = useState(null);
  const workedDaysSaveTimeoutsRef = useRef({});
  const workedDaysPeriodKeyRef = useRef(null);
  const workedDaysClearedKeysRef = useRef(new Set());

  // Загружаем ContentViewPlan, EmployeePlan, ListReportData и DampingCoefficients при открытии модалки
  useEffect(() => {
    if (open && pointSystem) {
      onContentViewPlansFetch();
      if (onEmployeePlansFetch) {
        onEmployeePlansFetch(pointSystem.id);
      }
      if (onDampingCoefficientsFetch) {
        onDampingCoefficientsFetch();
      }
      if (listId && onListReportDataFetch) {
        onListReportDataFetch();
      }
    }
  }, [open, pointSystem, listId, onContentViewPlansFetch, onEmployeePlansFetch, onDampingCoefficientsFetch, onListReportDataFetch]);

  // Получаем уникальные года из заполненных ContentViewPlan
  const availableYears = useMemo(() => {
    if (!contentViewPlans || contentViewPlans.length === 0) {
      return [];
    }
    const yearsSet = new Set(
      contentViewPlans
        .map((plan) => {
          const yearValue = plan.year;
          // Пробуем разные способы получения года
          if (typeof yearValue === 'number') {
            return yearValue;
          }
          if (typeof yearValue === 'string') {
            const num = parseInt(yearValue, 10);
            return !Number.isNaN(num) ? num : null;
          }
          return null;
        })
        .filter((y) => y !== null && y > 0)
    );
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [contentViewPlans]);

  // Получаем доступные месяцы для выбранного года
  const availableMonths = useMemo(() => {
    if (!contentViewPlans || contentViewPlans.length === 0 || !year) {
      return [];
    }
    // Приводим year к числу для корректного сравнения
    const yearNum = typeof year === 'number' ? year : parseInt(String(year), 10);
    if (Number.isNaN(yearNum)) {
      return [];
    }
    
    const filteredPlans = contentViewPlans.filter((plan) => {
      const planYear = typeof plan.year === 'number' 
        ? plan.year 
        : parseInt(String(plan.year), 10);
      const planMonth = typeof plan.month === 'number'
        ? plan.month
        : parseInt(String(plan.month), 10);
      
      return !Number.isNaN(planYear) && 
             !Number.isNaN(planMonth) && 
             planYear === yearNum && 
             planMonth >= 1 && 
             planMonth <= 12;
    });
    
    const monthsSet = new Set(
      filteredPlans
        .map((plan) => {
          const planMonth = typeof plan.month === 'number'
            ? plan.month
            : parseInt(String(plan.month), 10);
          return planMonth;
        })
        .filter((m) => !Number.isNaN(m) && m >= 1 && m <= 12)
    );
    return Array.from(monthsSet).sort((a, b) => a - b);
  }, [contentViewPlans, year]);

  // Формируем опции для годов - только заполненные
  const yearOptions = useMemo(() => {
    const yearsSet = new Set(availableYears);

    if (report?.years && Array.isArray(report.years)) {
      report.years.forEach((y) => {
        const yearNum = normalizePeriodValue(y);
        if (yearNum) {
          yearsSet.add(yearNum);
        }
      });
    }

    const savedYear = normalizePeriodValue(listReportData?.year);
    if (savedYear) {
      yearsSet.add(savedYear);
    }

    const selectedYear = normalizePeriodValue(year);
    if (selectedYear) {
      yearsSet.add(selectedYear);
    }

    return buildYearOptions(Array.from(yearsSet).sort((a, b) => b - a));
  }, [availableYears, report, listReportData, year]);

  // Формируем опции для месяцев - только заполненные для выбранного года
  const monthOptions = useMemo(() => {
    const monthsSet = new Set(availableMonths);

    const savedPeriod = normalizePeriod(listReportData?.year, listReportData?.month);
    const selectedYear = normalizePeriodValue(year);
    const selectedMonth = normalizePeriodValue(month);

    if (savedPeriod.year === selectedYear && savedPeriod.month) {
      monthsSet.add(savedPeriod.month);
    }

    if (selectedMonth) {
      monthsSet.add(selectedMonth);
    }

    return Array.from(monthsSet)
      .sort((a, b) => a - b)
      .map((monthValue) => ({
        key: monthValue,
        text:
          monthValue === selectedMonth
            ? `План расчета: ${MonthTitles[monthValue - 1] || `Месяц ${monthValue}`}`
            : MonthTitles[monthValue - 1] || `Месяц ${monthValue}`,
        value: monthValue,
      }));
  }, [availableMonths, listReportData, year, month]);

  // Восстанавливаем период из ListReportData при открытии модалки
  useEffect(() => {
    if (!open) {
      return;
    }

    const savedPeriod = normalizePeriod(listReportData?.year, listReportData?.month);
    if (!savedPeriod.year || !savedPeriod.month) {
      return;
    }

    setYear((currentYear) => (
      normalizePeriodValue(currentYear) === savedPeriod.year ? currentYear : savedPeriod.year
    ));
    setMonth((currentMonth) => (
      normalizePeriodValue(currentMonth) === savedPeriod.month ? currentMonth : savedPeriod.month
    ));
  }, [open, listReportData?.id, listReportData?.year, listReportData?.month]);

  // Сбрасываем месяц при изменении года, если выбранный месяц недоступен для нового года
  useEffect(() => {
    if (!year || !month) {
      return;
    }
    if (availableMonths.length > 0 && !availableMonths.includes(month)) {
      setMonth(null);
    }
  }, [year, availableMonths, month]);

  useEffect(() => {
    if (!open || !pointSystem || !listId || !year || !month) {
      return;
    }

    onReportFetch(year, month, listId, pointSystem.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pointSystem?.id, year, month, listId]);

  // Получаем ContentViewPlan для выбранного месяца/года
  const contentViewPlan = useMemo(() => {
    if (!contentViewPlans || !year || !month) {
      return null;
    }
    return contentViewPlans.find(
      (plan) => Number(plan.year) === Number(year) && Number(plan.month) === Number(month)
    ) || null;
  }, [contentViewPlans, year, month]);

  const contentTypeTitleByKey = useMemo(() => {
    const map = {
      photo: 'ФОТО',
      video: 'ВИДЕО',
      case: 'КЕЙСЫ',
    };

    (contentTypes || []).forEach((contentType) => {
      if (!contentType?.key) {
        return;
      }
      map[contentType.key] = contentType.title || map[contentType.key] || contentType.key;
    });

    return map;
  }, [contentTypes]);

  const listReportDataForPeriod = useMemo(() => {
    if (!listReportData || !year || !month) {
      return null;
    }
    if (Number(listReportData.year) === Number(year) && Number(listReportData.month) === Number(month)) {
      return listReportData;
    }
    return null;
  }, [listReportData, year, month]);

  const isMonthClosed = useMemo(() => {
    return listReportDataForPeriod?.closed === true;
  }, [listReportDataForPeriod]);

  const reportForPeriod = useMemo(() => {
    if (!report || !year || !month) {
      return null;
    }
    if (Number(report.year) === Number(year) && Number(report.month) === Number(month)) {
      return report;
    }
    return null;
  }, [report, year, month]);

  // Преобразуем незакрытые задачи в формат detailedTasks
  const unclosedTasksFormatted = useMemo(() => {
    if (!unclosedTasks || unclosedTasks.length === 0 || !year || !month) {
      return [];
    }

    // Дополнительная фильтрация по выбранному году и месяцу (на случай рассинхронизации)
    const filteredTasks = unclosedTasks.filter((task) => {
      const cardPointData = task.cardPointData;
      const createdAt = cardPointData?.createdAt || task.card?.createdAt;
      
      if (!createdAt) {
        return false;
      }
      
      const taskDate = new Date(createdAt);
      const taskYear = taskDate.getFullYear();
      const taskMonth = taskDate.getMonth() + 1; // getMonth() возвращает 0-11, нужно 1-12
      
      return taskYear === year && taskMonth === month;
    });

    return filteredTasks.map((task) => {
      const contentCardDefault = task.contentCardDefault;
      const cardPointData = task.cardPointData;
      const cardPointAdvancedData = task.cardPointAdvancedData || [];

      const typeKey = contentCardDefault?.type || '';
      const category = contentTypeTitleByKey[typeKey] || typeKey || '';
      const typeTitle = contentCardDefault?.name || '';
      const basePoints = Number(contentCardDefault?.value) || 0;
      const views = Number(cardPointData?.views) || 0;

      // Рассчитываем коэффициент используя логику из Card и List
      let coefficient = 1;
      
      // Если нет contentViewPlan, используем коэффициент 1 временно
      if (!contentViewPlan) {
        coefficient = 1;
      } else if (!contentCardDefault || !dampingCoefficients || dampingCoefficients.length === 0) {
        // Если нет contentCardDefault или dampingCoefficients, используем коэффициент 1
        coefficient = 1;
      } else if (contentCardDefault && cardPointData) {
        // Проверяем, есть ли подходящий диапазон DampingCoefficient
        // Повторяем логику подбора диапазона из calculateCoefficient()
        const INFINITY_VALUE = 1000000000;
        const isInfinity = (value) => value === null || value === INFINITY_VALUE;
        
        // Вычисляем viewsBase на основе типа
        let viewsBase = 0;
        viewsBase = getViewsBase(contentViewPlan, typeKey);
        
        const viewsReal = cardPointData.views ?? 0;
        const viewRange = viewsReal - viewsBase;
        
        // Вычисляем percent
        let percent;
        if (viewsBase <= 0) {
          percent = viewsReal === 0 ? 0 : Infinity;
        } else {
          percent = (viewRange / viewsBase) * 100;
        }
        
        // Ищем подходящий диапазон
        let matchingCoefficient = null;
        for (const dc of dampingCoefficients) {
          const from = dc.from;
          const to = dc.to;
          
          const effectiveFrom = from === INFINITY_VALUE ? -Infinity : (from !== null ? from : -100);
          const matchesFrom = from === INFINITY_VALUE ? true : percent >= effectiveFrom;
          const matchesTo = isInfinity(to) || percent <= to;
          
          if (matchesFrom && matchesTo) {
            matchingCoefficient = dc;
            break;
          }
        }
        
        // Если не найден подходящий диапазон, коэффициент = 0 (как в CardModal)
        if (!matchingCoefficient) {
          coefficient = 0;
        } else {
          // Рассчитываем коэффициент через calculateCoefficient
          coefficient = calculateCoefficient({
            contentCardDefault,
            cardPointData,
            contentViewPlan,
            dampingCoefficients,
          });
        }
      }

      // Преобразуем CardPointAdvancedData в формат extraPoints
      const extraPoints = (cardPointAdvancedData || []).map((item) => ({
        value: Number(item.value) || 0,
        description: item.description || '',
      }));

      const extraPointsSum = extraPoints.reduce((sum, p) => sum + (Number(p?.value) || 0), 0);
      const totalPoints = (basePoints + extraPointsSum) * coefficient;

      const createdAt = cardPointData?.createdAt || task.card?.createdAt;

      return {
        number: 0, // Будет установлен позже при объединении
        cardId: task.cardId,
        name: task.cardName || '',
        createdAt: formatDate(createdAt),
        closedAt: '', // Незакрытые задачи не имеют даты закрытия
        typeKey,
        category,
        type: typeTitle,
        basePoints,
        extraPoints,
        views,
        coefficient,
        totalPoints,
        comment: '',
        assignees: cardAssigneesByCardId?.[task.cardId] || [],
        isUnclosed: true, // Флаг для идентификации незакрытых задач
      };
    });
  }, [unclosedTasks, year, month, contentViewPlan, dampingCoefficients, cardAssigneesByCardId, contentTypeTitleByKey]);

  const detailedTasks = useMemo(() => {
    const closedTasks = [];
    const currentTemplateId = pointSystem?.sourcePointSystemId
      ? String(pointSystem.sourcePointSystemId)
      : null;

    // Закрытые задачи: значения из архива (на момент закрытия задачи), без live-пересчёта
    if (report && Array.isArray(report.detailedTasks)) {
      closedTasks.push(...report.detailedTasks.map((t, idx) => {
        const basePoints = Number(t.basePoints) || 0;
        const views = Number(t.views) || 0;
        const extraPoints = Array.isArray(t.extraPoints) ? t.extraPoints : [];
        const coefficient = Number(t.coefficient) || 0;
        const extraPointsSum = extraPoints.reduce((sum, p) => sum + (Number(p?.value) || 0), 0);
        const totalPoints = Number.isFinite(Number(t.totalPoints))
          ? Number(t.totalPoints)
          : (basePoints + extraPointsSum) * coefficient;
        const typeKey = t.typeKey || '';
        const sourceTemplateId = t.sourceTemplateId ? String(t.sourceTemplateId) : null;
        const isPreviousTemplate =
          Boolean(sourceTemplateId) &&
          Boolean(currentTemplateId) &&
          sourceTemplateId !== currentTemplateId;

        return {
          number: Number(t.number) || idx + 1,
          cardId: t.cardId || null,
          name: t.name || '',
          createdAt: formatDate(t.createdAt),
          closedAt: formatDate(t.closedAt),
          typeKey,
          category: t.category || contentTypeTitleByKey[typeKey] || typeKey || '',
          type: t.type || '',
          basePoints,
          extraPoints,
          views,
          coefficient,
          totalPoints,
          comment: t.comment || '',
          assignees: Array.isArray(t.assignees) && t.assignees.length > 0
            ? t.assignees
            : (cardAssigneesByCardId?.[t.cardId] || []),
          isUnclosed: false,
          sourceTemplateId,
          sourceTemplateName: t.sourceTemplateName || '',
          isPreviousTemplate,
        };
      }));
    }

    // Для закрытого периода показываем только архивные задачи
    const tasksForReport = isMonthClosed
      ? closedTasks
      : [...closedTasks, ...unclosedTasksFormatted];

    const allTasks = filterTasksByCurrentList(tasksForReport, currentListCardIds);

    // Перенумеровываем все задачи
    return allTasks.map((task, idx) => ({
      ...task,
      number: idx + 1,
    }));
  }, [
    report,
    unclosedTasksFormatted,
    isMonthClosed,
    contentTypeTitleByKey,
    cardAssigneesByCardId,
    currentListCardIds,
    pointSystem?.sourcePointSystemId,
  ]);

  const summaryByCategory = useMemo(() => {
    const normalizeItem = (item) => ({
      type: item.type || item.typeKey || null,
      title: item.title || item.category || item.type || '',
      count: Number(item.count) || 0,
      points: Number(item.points) || 0,
      views: Number(item.views) || 0,
    });

    // Закрытый период: статичный снимок с сервера (архив)
    if (isMonthClosed && Array.isArray(reportForPeriod?.summaryByCategory)) {
      return reportForPeriod.summaryByCategory.map(normalizeItem);
    }

    const categoryMap = new Map();

    const ensureEntry = (key, title) => {
      if (!key) {
        return null;
      }
      if (!categoryMap.has(key)) {
        categoryMap.set(key, {
          type: key,
          title: title || contentTypeTitleByKey[key] || key,
          count: 0,
          points: 0,
          views: 0,
        });
      }
      return categoryMap.get(key);
    };

    // Каркас блоков из серверной сводки / текущих типов контента
    if (Array.isArray(reportForPeriod?.summaryByCategory) && reportForPeriod.summaryByCategory.length > 0) {
      reportForPeriod.summaryByCategory.forEach((item) => {
        const key = item.type || item.title;
        ensureEntry(key, item.title);
      });
    } else {
      Object.keys(contentTypeTitleByKey).forEach((key) => {
        ensureEntry(key, contentTypeTitleByKey[key]);
      });
    }

    detailedTasks.forEach((task) => {
      const key = task.typeKey || task.category;
      const entry = ensureEntry(key, task.category);
      if (!entry) {
        return;
      }
      entry.count += 1;
      entry.points += Number(task.totalPoints) || 0;
      entry.views += Number(task.views) || 0;
    });

    return Array.from(categoryMap.values());
  }, [isMonthClosed, reportForPeriod, detailedTasks, contentTypeTitleByKey]);

  const totals = useMemo(() => {
    // Закрытый период: итоги из архива на сервере
    if (isMonthClosed && reportForPeriod) {
      const views = summaryByCategory.reduce((sum, c) => sum + (Number(c.views) || 0), 0);
      const defaultPoints = Number(reportForPeriod.defaultPoints) || 0;
      const extraPoints = Number(reportForPeriod.extraPoints) || 0;
      const coefficientPoints = Number(reportForPeriod.coefficientPoints) || 0;
      const workCount = Number.isFinite(Number(reportForPeriod.workCount))
        ? Number(reportForPeriod.workCount)
        : summaryByCategory.reduce((sum, c) => sum + (Number(c.count) || 0), 0);
      const totalPoints = Number.isFinite(Number(reportForPeriod.totalPoints))
        ? Number(reportForPeriod.totalPoints)
        : defaultPoints + extraPoints + coefficientPoints;

      return {
        workCount,
        views,
        totalPoints,
        defaultPoints,
        extraPoints,
        coefficientPoints,
      };
    }

    const workCount = summaryByCategory.reduce((sum, c) => sum + (Number(c.count) || 0), 0);
    const views = summaryByCategory.reduce((sum, c) => sum + (Number(c.views) || 0), 0);

    let defaultPoints = 0;
    let extraPoints = 0;
    let coefficientPoints = 0;

    detailedTasks.forEach((task) => {
      defaultPoints += task.basePoints;
      const extraPointsSum = task.extraPoints.reduce((sum, p) => sum + (Number(p?.value) || 0), 0);
      extraPoints += extraPointsSum;
      const taskCoefficientPoints = (task.basePoints + extraPointsSum) * (task.coefficient - 1);
      coefficientPoints += taskCoefficientPoints;
    });

    const totalPoints = defaultPoints + extraPoints + coefficientPoints;

    return {
      workCount,
      views,
      totalPoints,
      defaultPoints,
      extraPoints,
      coefficientPoints,
    };
  }, [isMonthClosed, reportForPeriod, summaryByCategory, detailedTasks]);

  // Форматируем задачи, закрытые не в выбранном месяце, но находящиеся в данном списке
  const tasksInListClosedInOtherMonth = useMemo(() => {
    if (!report || !Array.isArray(report.tasksInListClosedInOtherMonth)) {
      return [];
    }

    return filterTasksByCurrentList(
      report.tasksInListClosedInOtherMonth.map((t, idx) => {
        const creditPeriod = getMonthYearFromDate(t.closedAt);

        return {
          number: Number(t.number) || idx + 1,
          name: t.name || '',
          createdAt: formatDate(t.createdAt),
          closedAt: formatDate(t.closedAt),
          creditYear: creditPeriod?.year || null,
          creditMonth: creditPeriod?.month || null,
          category: t.category || '',
          type: t.type || '',
          basePoints: Number(t.basePoints) || 0,
          extraPoints: Array.isArray(t.extraPoints) ? t.extraPoints : [],
          views: Number(t.views) || 0,
          coefficient: Number(t.coefficient) || 0,
          totalPoints: Number(t.totalPoints) || 0,
          comment: t.comment || '',
          listId: t.listId || null,
          listName: t.listName || '',
          cardId: t.cardId || null,
        };
      }),
      currentListCardIds,
    );
  }, [report, currentListCardIds]);

  const listPeriodTitle = useMemo(
    () => formatMonthYearTitle(month, year),
    [month, year],
  );

  // Форматируем незакрытые задачи в текущем списке
  // Используем allUnclosedTasksInList, который содержит все задачи в списке,
  // у которых CardPointData.closed != true или нет CardPointData
  const unclosedTasksInCurrentList = useMemo(() => {
    if (!allUnclosedTasksInList || allUnclosedTasksInList.length === 0 || !listId) {
      return [];
    }

    return filterTasksByCurrentList(
      allUnclosedTasksInList.map((task) => ({
        name: task.cardName || '',
        cardId: task.cardId || null,
      })),
      currentListCardIds,
    );
  }, [allUnclosedTasksInList, listId, currentListCardIds]);

  const unclosedTasksInCurrentListForMonth = useMemo(
    () => filterTasksByCurrentList(unclosedTasks, currentListCardIds),
    [unclosedTasks, currentListCardIds],
  );

  // Итоги показываем только после завершения всех задач в списке (или для уже закрытого месяца)
  const areTotalsReady = useMemo(
    () => isMonthClosed || unclosedTasksInCurrentList.length === 0,
    [isMonthClosed, unclosedTasksInCurrentList.length],
  );

  const headerTitle = useMemo(() => {
    if (!month || !year) {
      return 'Отчёт';
    }
    const monthTitle = MonthTitles[month - 1] ? MonthTitles[month - 1].toLowerCase() : '';
    return `Отчёт за ${monthTitle} ${year}`;
  }, [month, year]);

  const plansForLevel = useMemo(() => {
    if (reportForPeriod?.employeePlans?.length > 0) {
      return reportForPeriod.employeePlans;
    }
    return employeePlans;
  }, [reportForPeriod, employeePlans]);

  const areEmployeeLevelsConfigured = useMemo(
    () => Array.isArray(plansForLevel) && plansForLevel.length > 0,
    [plansForLevel],
  );
  const shiftsPerPlan = useMemo(() => {
    const value = Number(pointSystem?.shiftsPerPlan);
    return Number.isFinite(value) && value > 0 ? value : 15;
  }, [pointSystem?.shiftsPerPlan]);

  const isReportPeriodSynced = useMemo(() => {
    if (!year || !month || !listReportDataForPeriod) {
      return false;
    }

    return (
      Number(listReportDataForPeriod.year) === Number(year) &&
      Number(listReportDataForPeriod.month) === Number(month)
    );
  }, [listReportDataForPeriod, year, month]);

  const closedDetailedTasks = useMemo(
    () => detailedTasks.filter((task) => !task.isUnclosed),
    [detailedTasks],
  );

  const previousTemplateNotices = useMemo(() => {
    if (Array.isArray(report?.previousTemplateStats) && report.previousTemplateStats.length > 0) {
      const total = closedDetailedTasks.length;
      return report.previousTemplateStats.map((entry) => ({
        count: Number(entry.count) || 0,
        total,
        templateName: entry.templateName || 'предыдущий шаблон',
      }));
    }

    const byTemplate = {};
    closedDetailedTasks.forEach((task) => {
      if (!task.isPreviousTemplate || !task.sourceTemplateId) {
        return;
      }
      const key = String(task.sourceTemplateId);
      if (!byTemplate[key]) {
        byTemplate[key] = {
          count: 0,
          templateName: task.sourceTemplateName || 'предыдущий шаблон',
        };
      }
      byTemplate[key].count += 1;
      if (task.sourceTemplateName) {
        byTemplate[key].templateName = task.sourceTemplateName;
      }
    });

    const total = closedDetailedTasks.length;
    return Object.values(byTemplate).map((entry) => ({
      ...entry,
      total,
    }));
  }, [report?.previousTemplateStats, closedDetailedTasks]);

  // Уровни сотрудников: баллы считаются по исполнителям закрытых задач в списке.
  const employeeLevels = useMemo(() => {
    if (!year || !month) {
      return [];
    }

    const savedLevels = Array.isArray(listReportDataForPeriod?.employeeLevels)
      ? listReportDataForPeriod.employeeLevels
      : [];

    if (!plansForLevel?.length || closedDetailedTasks.length === 0) {
      return savedLevels;
    }

    return computeEmployeeLevelsByAssignees(
      closedDetailedTasks,
      plansForLevel,
      savedLevels,
      shiftsPerPlan,
    );
  }, [listReportDataForPeriod, closedDetailedTasks, plansForLevel, shiftsPerPlan, year, month]);

  useEffect(() => {
    if (!open) {
      setWorkedDaysDrafts({});
      setWorkedDaysErrors({});
      setEmployeeLevelsValidationError(null);
      workedDaysPeriodKeyRef.current = null;
      workedDaysClearedKeysRef.current.clear();
      return;
    }

    if (!year || !month) {
      return;
    }

    const periodKey = `${year}-${month}`;
    const periodChanged = workedDaysPeriodKeyRef.current !== periodKey;
    workedDaysPeriodKeyRef.current = periodKey;

    if (periodChanged) {
      workedDaysClearedKeysRef.current.clear();
    }

    setWorkedDaysDrafts((prev) => {
      const next = periodChanged ? {} : { ...prev };

      employeeLevels.forEach((employee) => {
        const key = String(employee.userId);
        const savedValue =
          employee.workedDays !== null && employee.workedDays !== undefined && employee.workedDays !== ''
            ? String(employee.workedDays)
            : null;
        const userCleared = !periodChanged && workedDaysClearedKeysRef.current.has(key);

        if (userCleared) {
          next[key] = prev[key] ?? '';
          return;
        }

        if (savedValue !== null) {
          const currentDraft = periodChanged ? undefined : prev[key];
          if (
            currentDraft === undefined ||
            currentDraft === '' ||
            currentDraft === savedValue
          ) {
            next[key] = savedValue;
          } else {
            next[key] = currentDraft;
          }
        } else if (next[key] === undefined) {
          next[key] = '';
        }
      });

      return next;
    });
  }, [open, year, month, listReportDataForPeriod?.employeeLevels, employeeLevels]);

  const buildEmployeeLevelsPayload = useCallback(
    (draftsOverride = workedDaysDrafts) =>
      employeeLevels.map((employee) => {
        const userId = String(employee.userId);
        const draftValue = draftsOverride[userId];
        let workedDays = null;

        if (draftValue === '') {
          workedDays = null;
        } else if (draftValue !== undefined && isValidWorkedDaysValue(draftValue)) {
          workedDays = Number(normalizeWorkedDaysInput(draftValue));
        } else if (isValidWorkedDaysValue(employee.workedDays)) {
          workedDays = Number(employee.workedDays);
        }

        return {
          userId,
          name: employee.name || '',
          organization: employee.organization || '',
          totalPoints: Number(employee.totalPoints) || 0,
          workCount: Number(employee.workCount) || 0,
          workedDays,
          level:
            workedDays === null
              ? null
              : computeEmployeeLevel(plansForLevel, employee.totalPoints, workedDays, shiftsPerPlan),
        };
      }),
    [employeeLevels, workedDaysDrafts, plansForLevel, shiftsPerPlan],
  );

  const saveEmployeeWorkedDays = useCallback(
    (userId, draftsOverride = workedDaysDrafts) => {
      if (
        !onListReportEmployeeLevelsUpdate ||
        !year ||
        !month ||
        !isReportPeriodSynced
      ) {
        return;
      }

      const employee = employeeLevels.find((entry) => String(entry.userId) === String(userId));
      if (!employee) {
        return;
      }

      const draftValue = draftsOverride[String(userId)];
      const isCleared = draftValue === null || draftValue === '';

      if (draftValue === undefined || (!isCleared && !isValidWorkedDaysValue(draftValue))) {
        return;
      }

      onListReportEmployeeLevelsUpdate({
        userId: String(userId),
        workedDays: isCleared ? null : Number(normalizeWorkedDaysInput(draftValue)),
        year,
        month,
        employeeLevels: buildEmployeeLevelsPayload(draftsOverride),
      });
    },
    [
      onListReportEmployeeLevelsUpdate,
      isReportPeriodSynced,
      year,
      month,
      employeeLevels,
      workedDaysDrafts,
      buildEmployeeLevelsPayload,
    ],
  );

  const handleWorkedDaysChange = useCallback(
    (userId, rawValue) => {
      const key = String(userId);
      const normalized = normalizeWorkedDaysInput(rawValue);

      if (normalized !== '' && !/^\d*(\.\d*)?$/.test(normalized)) {
        return;
      }

      if (normalized === '') {
        workedDaysClearedKeysRef.current.add(key);
      } else if (isValidWorkedDaysValue(normalized)) {
        workedDaysClearedKeysRef.current.delete(key);
      }

      setWorkedDaysDrafts((prev) => {
        const nextDrafts = {
          ...prev,
          [key]: normalized,
        };

        if (workedDaysSaveTimeoutsRef.current[key]) {
          clearTimeout(workedDaysSaveTimeoutsRef.current[key]);
        }

        if (
          (normalized === '' || isValidWorkedDaysValue(normalized)) &&
          onListReportEmployeeLevelsUpdate
        ) {
          workedDaysSaveTimeoutsRef.current[key] = setTimeout(() => {
            saveEmployeeWorkedDays(key, nextDrafts);
          }, 400);
        }

        return nextDrafts;
      });

      setWorkedDaysErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setEmployeeLevelsValidationError(null);
    },
    [onListReportEmployeeLevelsUpdate, saveEmployeeWorkedDays],
  );

  useEffect(
    () => () => {
      Object.values(workedDaysSaveTimeoutsRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
    },
    [],
  );

  const handleWorkedDaysSave = useCallback(
    (userId) => {
      const key = String(userId);
      const draftValue = workedDaysDrafts[key];

      if (draftValue === '') {
        workedDaysClearedKeysRef.current.add(key);
        setWorkedDaysErrors((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });

        if (workedDaysSaveTimeoutsRef.current[key]) {
          clearTimeout(workedDaysSaveTimeoutsRef.current[key]);
          delete workedDaysSaveTimeoutsRef.current[key];
        }

        if (onListReportEmployeeLevelsUpdate) {
          saveEmployeeWorkedDays(key);
        }
        return;
      }

      if (!isValidWorkedDaysValue(draftValue)) {
        setWorkedDaysErrors((prev) => ({
          ...prev,
          [key]: true,
        }));
        return;
      }

      setWorkedDaysErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      if (onListReportEmployeeLevelsUpdate) {
        saveEmployeeWorkedDays(key);
      }
    },
    [workedDaysDrafts, onListReportEmployeeLevelsUpdate, saveEmployeeWorkedDays],
  );

  const hasMissingWorkedDays = useMemo(
    () =>
      employeeLevels.length > 0 &&
      employeeLevels.some((employee) => !isValidWorkedDaysValue(workedDaysDrafts[String(employee.userId)] ?? employee.workedDays)),
    [employeeLevels, workedDaysDrafts],
  );

  const handleCompleteMonthConfirm = useCallback(() => {
    if (isMonthClosed) {
      return;
    }

    if (hasMissingWorkedDays) {
      setEmployeeLevelsValidationError('Заполните количество отработанных дней для каждого сотрудника');
      return;
    }

    if (unclosedTasksInCurrentListForMonth.length > 0) {
      // Открываем модалку с незакрытыми задачами
      if (onOpenUnclosedTasksModal) {
        onOpenUnclosedTasksModal(unclosedTasksInCurrentListForMonth);
      }
    } else if (onCompleteMonth) {
      onCompleteMonth();
      onClose();
    }
  }, [isMonthClosed, hasMissingWorkedDays, unclosedTasksInCurrentListForMonth, onCompleteMonth, onClose, onOpenUnclosedTasksModal]);

  return (
    <Modal
      open={open}
      closeIcon
      size="large"
      centered={false}
      className={styles.modal}
      onClose={onClose}
    >
      <Modal.Header className={styles.modalHeader}>
        <div className={styles.modalHeaderInner}>
          <div className={styles.modalTitle}>{headerTitle}</div>
          <div className={styles.periodControls}>
            <div className={styles.periodLabel}>Период расчёта плана</div>
            <Form className={styles.periodForm}>
              <Form.Select
                options={yearOptions}
                value={year}
                placeholder="Год"
                onChange={(_, { value }) => {
                  setYear(value);
                  if (value && month && listId && pointSystem) {
                    onListReportDataUpsert({
                      listId,
                      year: value,
                      month,
                    });
                  }
                }}
                className={`${styles.yearSelect} ${!year ? styles.selectError : ''}`}
              />
              <Form.Select
                options={monthOptions}
                value={month}
                placeholder="Месяц"
                onChange={(_, { value }) => {
                  setMonth(value);
                  if (year && value && listId && pointSystem) {
                    onListReportDataUpsert({
                      listId,
                      year,
                      month: value,
                    });
                  }
                }}
                className={`${styles.monthSelect} ${!month ? styles.selectError : ''}`}
              />
            </Form>
          </div>
        </div>
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <div className={styles.sections}>
          {!year || !month ? (
            <div className={styles.noDataMessage}>НЕТ ДАННЫХ</div>
          ) : (
            <>
              <div className={styles.section}>
                <div className={styles.sectionHeading}>Итоги месяца</div>
                {areTotalsReady ? (
                  <>
                    <div className={styles.totalsCard}>
                      <div className={styles.totalsMetrics}>
                        <div className={styles.metric}>
                          <div className={styles.metricValue}>{formatPoints(totals.totalPoints)}</div>
                          <div className={styles.metricLabel}>Итого баллов</div>
                        </div>
                        <div className={styles.metric}>
                          <div className={styles.metricValue}>{totals.workCount}</div>
                          <div className={styles.metricLabel}>Количество работ</div>
                        </div>
                        <div className={styles.metric}>
                          <div className={styles.metricValue}>{totals.views}</div>
                          <div className={styles.metricLabel}>Просмотры</div>
                        </div>
                      </div>
                      <div className={styles.totalsBreakdown}>
                        <StatRow label="Баллы по умолчанию" value={formatPoints(totals.defaultPoints)} />
                        <StatRow label="Дополнительные баллы" value={formatPoints(totals.extraPoints)} />
                        <StatRow
                          label="Баллы по коэффициенту"
                          value={formatPoints(totals.coefficientPoints)}
                        />
                      </div>
                    </div>
                    {isReportLoading && <div className={styles.loadingHint}>Загрузка...</div>}

                    <div className={styles.categoriesGrid}>
                      {summaryByCategory.map((c) => (
                        <CategorySummaryCard
                          key={c.type || c.title}
                          title={c.title}
                          count={c.count}
                          points={c.points}
                          views={c.views}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className={styles.totalsPendingMessage}>
                    Итоги появятся, когда все задачи в списке будут завершены
                  </div>
                )}

                <div className={styles.employeeLevelBlock}>
                  <div className={styles.employeeLevelsTitle}>Уровни сотрудников</div>
                  {employeeLevelsValidationError && (
                    <div className={styles.employeeLevelsValidationError}>
                      {employeeLevelsValidationError}
                    </div>
                  )}
                  {!areEmployeeLevelsConfigured ? (
                    <div className={styles.employeeLevelsEmpty}>
                      В настройках балльной системы не заданы уровни сотрудников
                    </div>
                  ) : employeeLevels.length === 0 ? (
                    <div className={styles.employeeLevelsEmpty}>—</div>
                  ) : (
                    <div className={styles.employeeLevelsTableWrapper}>
                      <table className={styles.employeeLevelsTable}>
                        <thead>
                          <tr>
                            <th>Сотрудник</th>
                            <th>Отработано дней</th>
                            <th>Среднее кол-во баллов</th>
                            <th>Уровень</th>
                          </tr>
                        </thead>
                        <tbody>
                          {employeeLevels.map((employee) => {
                            const userId = String(employee.userId);
                            const hasDraft = Object.prototype.hasOwnProperty.call(
                              workedDaysDrafts,
                              userId,
                            );
                            const draftValue = hasDraft
                              ? workedDaysDrafts[userId]
                              : employee.workedDays !== null &&
                                  employee.workedDays !== undefined &&
                                  employee.workedDays !== ''
                                ? String(employee.workedDays)
                                : '';
                            const hasError = workedDaysErrors[userId];
                            const workedDaysSource = !hasDraft
                              ? employee.workedDays
                              : draftValue === ''
                                ? null
                                : isValidWorkedDaysValue(draftValue)
                                  ? draftValue
                                  : employee.workedDays;
                            const workedDays = isValidWorkedDaysValue(workedDaysSource)
                              ? Number(normalizeWorkedDaysInput(workedDaysSource))
                              : null;
                            const averagePoints =
                              workedDays !== null && workedDays > 0
                                ? Math.round((Number(employee.totalPoints) || 0) / workedDays)
                                : null;
                            const level =
                              workedDays === null
                                ? null
                                : computeEmployeeLevel(
                                    plansForLevel,
                                    employee.totalPoints,
                                    workedDays,
                                    shiftsPerPlan,
                                  );

                            return (
                              <tr key={userId}>
                                <td>
                                  <div className={styles.employeeName}>
                                    {employee.name || `Сотрудник ${userId}`}
                                  </div>
                                  {employee.organization ? (
                                    <div className={styles.employeeRole}>{employee.organization}</div>
                                  ) : null}
                                </td>
                                <td>
                                  <Form.Input
                                    value={draftValue}
                                    onChange={(_, { value }) => handleWorkedDaysChange(userId, value)}
                                    onBlur={() => handleWorkedDaysSave(userId)}
                                    className={`${styles.workedDaysInput} ${
                                      hasError ? styles.workedDaysInputError : ''
                                    }`}
                                    placeholder="Введите дни"
                                    title="Укажите, сколько дней сотрудник фактически отработал за этот месяц"
                                  />
                                </td>
                                <td>
                                  {averagePoints !== null && averagePoints !== undefined
                                    ? averagePoints
                                    : '—'}
                                </td>
                                <td>{level !== null && level !== undefined ? level : '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {tasksWithoutAssignee.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionTitleCentered}>Задачи без исполнителя</div>
                  <div className={styles.taskBadgesContainer}>
                    {tasksWithoutAssignee.map((t, idx) => (
                      <TaskNameOnly
                        key={`no-assignee-${t.cardId || idx}`}
                        task={t}
                        linkState={{ warnMissingAssignee: true }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {tasksInListClosedInOtherMonth.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionTitleCentered}>Задачи с другим месяцем зачёта</div>
                  <div className={styles.otherMonthTasksList}>
                    {tasksInListClosedInOtherMonth.map((t) => (
                      <OtherMonthTaskItem
                        key={`other-month-${t.cardId || t.number}`}
                        task={t}
                        listMonthTitle={listPeriodTitle}
                      />
                    ))}
                  </div>
                </div>
              )}

              {unclosedTasksInCurrentList.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionTitleCentered}>Незакрытые задачи в списке</div>
                  <div className={styles.taskBadgesContainer}>
                    {unclosedTasksInCurrentList.map((t, idx) => (
                      <TaskNameOnly key={`unclosed-${t.cardId || idx}`} task={t} />
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.section}>
                <div className={styles.sectionTitleCentered}>Детализированный отчёт за месяц</div>
                {previousTemplateNotices.length > 0 && (
                  <div className={styles.previousTemplateNotices}>
                    {previousTemplateNotices.map((notice) => (
                      <div
                        key={`${notice.templateName}-${notice.count}`}
                        className={styles.previousTemplateNotice}
                      >
                        {`${notice.count} задач из ${notice.total} закрыты в шаблоне ${notice.templateName}`}
                      </div>
                    ))}
                  </div>
                )}
                <div className={styles.tasks}>
                  {detailedTasks.length > 0 ? (
                    detailedTasks.map((t) => <TaskBlock key={t.number} task={t} />)
                  ) : (
                    <div className={styles.emptyMessage}>
                      {isReportLoading ? 'Загрузка...' : 'Нет данных для отображения'}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
});

MonthlyReportModal.propTypes = {
  open: PropTypes.bool,
  /* eslint-disable react/forbid-prop-types */
  pointSystem: PropTypes.object,
  listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  report: PropTypes.object,
  unclosedTasks: PropTypes.array,
  allUnclosedTasksInList: PropTypes.array,
  contentViewPlans: PropTypes.array,
  employeePlans: PropTypes.array,
  dampingCoefficients: PropTypes.array,
  contentTypes: PropTypes.array,
  cardAssigneesByCardId: PropTypes.object,
  tasksWithoutAssignee: PropTypes.array,
  currentListCardIds: PropTypes.instanceOf(Set),
  /* eslint-enable react/forbid-prop-types */
  isReportLoading: PropTypes.bool.isRequired,
  onReportFetch: PropTypes.func.isRequired,
  onContentViewPlansFetch: PropTypes.func.isRequired,
  onEmployeePlansFetch: PropTypes.func,
  onDampingCoefficientsFetch: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onCompleteMonth: PropTypes.func,
  onOpenUnclosedTasksModal: PropTypes.func,
  onListReportDataUpsert: PropTypes.func,
  onListReportDataFetch: PropTypes.func,
  onListReportEmployeeLevelsUpdate: PropTypes.func,
  listReportData: PropTypes.object,
};

MonthlyReportModal.defaultProps = {
  open: true,
  pointSystem: null,
  listId: null,
  report: null,
  unclosedTasks: [],
  allUnclosedTasksInList: [],
  contentViewPlans: [],
  employeePlans: [],
  dampingCoefficients: [],
  contentTypes: [],
  cardAssigneesByCardId: {},
  tasksWithoutAssignee: [],
  currentListCardIds: new Set(),
  onEmployeePlansFetch: null,
  onCompleteMonth: null,
  onOpenUnclosedTasksModal: null,
  onListReportDataUpsert: null,
  onListReportDataFetch: null,
  onListReportEmployeeLevelsUpdate: null,
  listReportData: null,
};

export default MonthlyReportModal;

